import * as angular from 'angular';
import * as _ from 'lodash';

import * as Actions from './tags.constants';

import { TagDefinition } from '../../models';
import { ITagDefinitionService } from '../../services';
import { Dispatcher, GetStore, Dispatch } from '../Store';
import { IValueViewModelActionsService } from '../valueViewModels/valueViewModels.actionCreator';

export interface ITagDefinitionActionsService {
    getAll(): Dispatcher<TagDefinition[]>;
    getAll(force: boolean): Dispatcher<TagDefinition[]>;
    getOne(guid: string): Dispatcher<TagDefinition>;
    update(tags: TagDefinition[]): Dispatcher<TagDefinition[]>;
    create(tag: TagDefinition): Dispatcher<TagDefinition>;
    delete(guids: string[]): Dispatcher<TagDefinition[]>;
    forceInMemoryUpdate(updated: TagDefinition[]): Dispatcher<TagDefinition[]>;
}

export class TagDefinitionActionsService implements ITagDefinitionActionsService {

    static serviceId = 'tagDefinitionActionsService';
    static $inject = [
        '$q',
        'tagDefinitionService',
        'valueViewModelActionsService'
    ];

    constructor(private _$q: angular.IQService,
        private _tagDefinitionService: ITagDefinitionService,
        private _valueViewModelActionsService: IValueViewModelActionsService
    ) {
    }

    getAll();
    getAll(force: boolean);
    getAll(force?: boolean): Dispatcher<TagDefinition[]> {
        return (dispatch: Dispatch<TagDefinition[]>, getState: GetStore) => {
            const { tags } = getState();
            if (force || (!tags.isLoading && !tags.isLoaded)) {
                dispatch({ type: Actions.TAGS_LOAD });
                return this._tagDefinitionService
                    .getAll(force)
                    .then(data => {
                        const resultsDto = data;
                        const results = data.map(d => d.tagDefinition);
                        dispatch({ type: Actions.TAGS_DTO_LOAD_SUCCESS, payload: resultsDto as any[] });
                        dispatch({ type: Actions.TAGS_LOAD_SUCCESS, payload: results });
                        return results;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.TAGS_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(_.values<TagDefinition>(tags.dictionary));
            }
        };
    }

    getOne(guid: string): Dispatcher<TagDefinition> {
        return (dispatch: Dispatch<TagDefinition>, getState: GetStore) => {
            const { tags } = getState();
            if (!tags.isLoading && !tags.isLoaded) {
                dispatch({ type: Actions.TAG_LOAD });
                return this._tagDefinitionService
                    .getByGuid(guid)
                    .then(dto => {
                        dispatch({ type: Actions.TAG_DTO_LOAD_SUCCESS, payload: dto as any });
                        dispatch({ type: Actions.TAG_LOAD_SUCCESS, payload: dto.tagDefinition });
                        return dto.tagDefinition;
                    })
                    .catch(reason => {
                        dispatch({ type: Actions.TAG_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(_.find(_.values<TagDefinition>(tags.dictionary), c => c.guid === guid));
            }
        };
    }

    update(tags: TagDefinition[]): Dispatcher<TagDefinition[]> {
        return (dispatch: Dispatch<TagDefinition[]>, getStore: GetStore) => {
            dispatch({ type: Actions.TAGS_LOAD });
            return this._tagDefinitionService
                .update(tags)
                .then(tags => this._tagDefinitionService.getByGuids(tags.map(t => t.guid)))
                .then(tagsDto => {
                    dispatch({ type: Actions.TAGS_DTO_UPDATE_SUCCESS, payload: tagsDto as any[] });
                    const tags = tagsDto.map(t => t.tagDefinition);
                    dispatch({ type: Actions.TAGS_UPDATE_SUCCESS, payload: tags });
                    // should it be sync ?
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    return tags;
                })
                .catch(reason => {
                    dispatch({ type: Actions.TAGS_UPDATE_ERROR, payload: reason });
                    throw reason;
                });
        };
    }

    create(tag: TagDefinition): Dispatcher<TagDefinition> {
        return (dispatch: Dispatch<any>, getStore: GetStore) => {
            dispatch({ type: Actions.TAG_LOAD });
            return this._tagDefinitionService
                .add(tag)
                .then(tag => {
                    dispatch({ type: Actions.TAG_CREATE_SUCCESS, payload: tag });
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    this._tagDefinitionService.getByGuid(tag.guid)
                        .then(dto => {
                            dispatch({ type: Actions.TAG_DTO_LOAD_SUCCESS, payload: dto });
                        });
                    return tag;
                })
                .catch(reason => {
                    dispatch({ type: Actions.TAG_CREATE_ERROR, payload: reason });
                    throw reason;
                });
        };
    }

    delete(guids: string[]): Dispatcher<TagDefinition[]> {
        return (dispatch: Dispatch<TagDefinition[]>, getStore: GetStore) => {
            return this._tagDefinitionService
                .deleteTagList(guids)
                .then(deleted => {
                    this._valueViewModelActionsService.getAll(true)(dispatch, getStore);
                    // deleted parameters seems to be empty :(
                    dispatch({ type: Actions.TAGS_DELETE_SUCCESS, payload: deleted });
                    return deleted;
                })
                .catch(reason => {
                    dispatch({ type: Actions.TAGS_DELETE_ERROR, payload: reason });
                    throw reason;
                });
        };
    }

    forceInMemoryUpdate(updated: TagDefinition[]): Dispatcher<TagDefinition[]> {
        return (dispatch: Dispatch<TagDefinition[]>, getState: GetStore) => {
            dispatch({ type: Actions.TAGS_SAVE_SUCCESS, payload: updated });
            return this._valueViewModelActionsService
                .getAll(true)(dispatch, getState)
                .then(() => this._$q.resolve(updated));
        };
    }
}